import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { QuoteService } from '@app/quoting/services/quote.service';
import { QuoteRequestState } from '@app/shared';
import { Observable, Subscription } from 'rxjs';
import { Logger } from '@app/logging.service';
import { Store } from '@ngrx/store';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { AppState } from '@app/state/app.state';
import { isReadOnlySelector, quoteSelector } from '@app/state/app.selectors';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';

@Component({
  selector: 'app-add-quote',
  templateUrl: './add-quote.component.html',
  styleUrls: ['./add-quote.component.scss'],
})
export class AddQuoteComponent implements OnInit, OnDestroy {
  @Input() quoteRequestState: QuoteRequestState;
  @Input() submitEvent: Observable<void>;
  @Input() userIdentity: UtilityHubIdentity;

  @Output() quoteSubmitted = new EventEmitter<boolean>();

  public quoteForm: FormGroup;
  private submit$: Subscription;
  private quote$: Subscription;
  private isReadOnly$: Subscription;

  private logger: Logger;

  constructor(
    private fb: FormBuilder,
    private quoteService: QuoteService,
    private quoteRequestService: QuoteRequestService,
    private store: Store<AppState>
  ) {
    this.logger = Logger.for(this);
  }

  ngOnInit(): void {
    this.submit$ = this.submitEvent.subscribe(() => this.submitQuote());
    this.quoteForm = this.fb.group({
      term: ['', [Validators.required, Validators.min(0), Validators.max(48)]],
      rate: ['', [Validators.required]],
    });

    this.quote$ = this.store.select(quoteSelector).subscribe((quoteState) => {
      const quote = quoteState.quote;

      if (quote?.prices.length > 0) {
        this.quoteForm.patchValue({
          term: quote.prices[0].term,
          rate: quote.prices[0].sellingPrice,
        });
      }

      this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
        if(isReadOnly) {
          this.quoteForm.disable({ emitEvent: false });
        }
      });
    });
  }

  ngOnDestroy() {
    this.submit$?.unsubscribe();
    this.quote$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  getEnergyUnits(): string {
    return this.quoteRequestState?.uom;
  }

  isReadOnly() {
    return this.quoteRequestService.isReadonly(
      this.quoteRequestState?.quoteRequestId, 
      this.quoteRequestState?.status, 
      this.userIdentity?.id, 
      this.quoteRequestState?.ownerUserId);
  }

  submitQuote(): void {
    this.quoteForm.markAllAsTouched();
    if (!this.quoteForm.valid) {
      return;
    }

    this.quoteService.addQuote(this.quoteRequestState.quoteRequestId, this.quoteForm.value.term, this.quoteForm.value.rate).subscribe(
      () => {
        this.quoteSubmitted.emit(true);
      },
      (err) => {
        this.logger.error(err);
      }
    );
  }
}
